import { combineLatest, map, Observable, of } from 'rxjs'
import url from 'libraries/front-end-core/core/url'
import { RESPONSE_NOTFOUND } from 'libraries/front-end-core/constants/responses'
import { fetch } from 'services/ApiService'
import {
  AUTOMATED_DOCUMENTS,
  AUTOMATED_DOCUMENTS_PREPAY,
  CLARILIS_DOCUMENTS,
  DOCUMENT_SECTIONS,
  DOCUMENTS,
  DOCUMENTS_PREPAY
} from 'constants/requests'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import { toSections } from 'requests/document/transform'
import { getTopLevelSections } from './QAService'
import { DOCUMENTS_CONTENT } from 'constants/controllers'
import { AOL_CORPORATE } from 'constants/aol/aol'

export interface DocumentsSectionsResponse {
  lastUpdate: string
  bookmarkId: string
  id: string
  title: string
  parentId: string
  active: boolean
}

export interface DocumentResponse {
  hasAccess: boolean
  data: {
    id: string
    title: string
    fileName: string
    description: string | null
    parentId: string
    owningAol: string
    aols: string[]
    tilePositions: unknown[]
    published: boolean
    underMaintenance: boolean
    links: unknown[]
    previewAvailable: boolean
  }
}

export interface FlatDocumentSection {
  active: boolean
  childSections: string[]
  descendants: string[]
  id: string
  overview: null
  path: string[]
  title: string
  areaOfLaw: 'corporate'
}

export interface AutomatedDocumentResponseData {
  id: number
  uuid: string
  title: string
  description: null | string
  seoUrl: string
  overview: null | string
  parentSectionId: string
  automated: boolean
  isPublished: boolean
  underMaintenance: boolean
}

export interface AutomatedDocumentResponse {
  hasAccess: boolean
  data: AutomatedDocumentResponseData
}

interface ClarilisDocumentData extends AutomatedDocumentResponseData {
  ctaLink: string
}

export interface ClarilisDocumentResponse {
  hasAccess: boolean
  data: ClarilisDocumentData
}

export interface Document {
  id: string
  title: string
  description: string | null
  parentId: string
  automated: boolean
  clarilis: boolean
  hasAccess: boolean
  underMaintenance: boolean
  previewAvailable?: boolean
  links?: unknown[]
  cta?: string
  position: number
}

export type SectionWithDocuments = FlatDocumentSection & { documents: Document[] }
export type SectionWithChildren = FlatDocumentSection & { sections: SectionWithDocuments[] }

type TransformedDocumentSection = ReturnType<typeof toSections>

export interface DocumentSearchResultItem {
  id: string
  name: string
  content?: string | null
  properties: {
    title: string
    description: string
    library: string
    parentId: string
    position: string
  }
  tiers: string[]
  lastUpdateTime?: number
  automated?: boolean
}

export interface DocumentsSearchResponse {
  count: number
  results: DocumentSearchResultItem[]
  facets: Array<{
    name: string
    count: number
  }>
}

export function requestDocumentSections (
  aol = 'corporate',
  filterEmptySections = false,
  filterAutomatedDocuments = false
): Observable<number | (TransformedDocumentSection & { topLevelSections: Array<TransformedDocumentSection['nodes'][number]> })> {
  return combineLatest(
    fetch(DOCUMENT_SECTIONS, { aol }, {}),
    filterEmptySections ? requestAllDocuments(aol) : of([])
  ).pipe(
    map(([sectionsResponse, documentsResponse]: [number | TransformedDocumentSection, number | Document[]]) => {
      if (typeof sectionsResponse === 'number') return sectionsResponse
      if (typeof documentsResponse === 'number') return documentsResponse

      if (filterEmptySections) {
        const sectionsWithDocuments = new Set<string>()
        for (const doc of documentsResponse) {
          if (filterAutomatedDocuments && !doc.automated) continue
          sectionsWithDocuments.add(doc.parentId)
        }

        const nodes = Object.fromEntries(
          Object.entries(sectionsResponse.nodes).filter(([sectionId, section]) => {
            const hasDocuments = sectionsWithDocuments.has(sectionId)
            const hasChildWithDocuments = section.childSections.some(childId => sectionsWithDocuments.has(childId))
            return hasDocuments || hasChildWithDocuments
          })
        )
        const topLevelSections = (getTopLevelSections(nodes, sectionsResponse.root) as unknown as FlatDocumentSection[])
          .map((section) => ({
            ...section,
            dull: false
          }))

        return {
          ...sectionsResponse,
          nodes,
          topLevelSections
        }
      }
      const topLevelSections = getTopLevelSections(sectionsResponse.nodes, sectionsResponse.root)

      return {
        ...sectionsResponse,
        topLevelSections
      }
    })
  )
}

export function requestDocuments (): Observable<number | Document[]> {
  return loggedRequestSwitcher<Document[], Document[]>(
    () => fetch(DOCUMENTS, {}, {}),
    () => fetch(DOCUMENTS_PREPAY, {}, {})
  )
}

export function requestAutomatedDocuments (): Observable<number | Document[]> {
  return loggedRequestSwitcher<Document[], Document[]>(
    () => fetch(AUTOMATED_DOCUMENTS, {}, {}),
    () => fetch(AUTOMATED_DOCUMENTS_PREPAY, {}, {})
  )
}

export function requestClarilisDocuments (): Observable<number | Document[]> {
  return loggedRequestSwitcher<Document[], Document[]>(
    () => fetch(CLARILIS_DOCUMENTS, {}, {}),
    () => of([])
  )
}

export function requestAllDocuments (_aol = 'corporate'): Observable<number | Document[]> {
  return combineLatest([
    requestDocuments(),
    requestAutomatedDocuments(),
    requestClarilisDocuments()
  ]).pipe(
    map((responses) => {
      if (responses.some(response => typeof response === 'number')) {
        return responses.find(response => typeof response === 'number') as number
      }
      const [documents, automatedDocuments, clarilisDocuments] = responses as [Document[], Document[], Document[]]

      return [...documents, ...automatedDocuments, ...clarilisDocuments]
    })
  )
}

export function requestSectionDocuments (
  sectionId: string,
  aol = 'corporate',
  filterAutomatedDocuments = false
): Observable<number | SectionWithChildren> {
  return combineLatest(
    requestDocumentSections(aol),
    requestAllDocuments(aol)
  ).pipe(
    map((responses) => {
      const [sections, documents] = responses
      if (typeof sections === 'number') return sections
      if (typeof documents === 'number') return documents

      const section = sections.topLevelSections.find(section => section.id === sectionId)
      if (section === undefined) return RESPONSE_NOTFOUND

      const byParent = new Map<string, Document[]>()
      for (const doc of documents) {
        if (filterAutomatedDocuments && !doc.automated) continue
        const bucket = byParent.get(doc.parentId)
        if (bucket != null) bucket.push(doc)
        else byParent.set(doc.parentId, [doc])
      }

      return {
        ...section,
        sections: section.childSections.reduce<SectionWithDocuments[]>((acc, curr) => {
          const child = sections.nodes[curr]
          const sectionDocuments = byParent.get(curr)
          if (sectionDocuments == null) return acc
          return [...acc, {
            ...child,
            documents: sectionDocuments.sort((a, b) => {
              if (a.automated && !b.automated) return -1
              if (!a.automated && b.automated) return 1
              return a.position - b.position
            })
          }]
        }, [])
      }
    })
  )
}

type AllSectionDocumentsObservable = Observable<number | SectionWithChildren[]>

const allSectionDocumentsCache = new Map<string, AllSectionDocumentsObservable>()

export function requestAllSectionDocuments (aol = 'corporate', filterAutomatedDocuments = false): AllSectionDocumentsObservable {
  const cacheKey = `${aol}-${String(filterAutomatedDocuments)}`
  const cached = allSectionDocumentsCache.get(cacheKey)
  if (cached !== undefined) {
    return cached
  }

  const obs$ = combineLatest(
    requestDocumentSections(aol),
    requestAllDocuments(aol)
  ).pipe(
    map((responses): number | SectionWithChildren[] => {
      const [sections, documents] = responses
      if (typeof sections === 'number') return sections
      if (typeof documents === 'number') return documents

      const byParent = new Map<string, Document[]>()
      for (const doc of documents) {
        if (filterAutomatedDocuments && !doc.automated) continue
        const bucket = byParent.get(doc.parentId)
        if (bucket != null) {
          bucket.push(doc)
        } else {
          byParent.set(doc.parentId, [doc])
        }
      }

      return sections.topLevelSections.map(section => ({
        ...section,
        sections: section.childSections.reduce<SectionWithDocuments[]>((acc, curr) => {
          const childSection = sections.nodes[curr]
          const sectionDocuments = byParent.get(curr)
          if (sectionDocuments == null) return acc
          return [...acc, { ...childSection, documents: sectionDocuments }]
        }, [])
      })).filter(section => section.sections.length > 0)
    })
  )

  allSectionDocumentsCache.set(cacheKey, obs$)
  return obs$
}

export function requestSharedDocuments (sectionId: string, documentIds: string[], aol = 'corporate'): Observable<number | SectionWithChildren> {
  return requestAllSectionDocuments(aol).pipe(
    map((response) => {
      if (typeof response === 'number') return response

      const section = response.find(section => section.id === sectionId)
      if (section === undefined) return RESPONSE_NOTFOUND

      return {
        ...section,
        sections: section.sections
          .map(subsection => ({
            ...subsection,
            documents: subsection.documents.filter((document: Document) => documentIds.includes(document.id))
          }))
          .filter(subsection => subsection.documents.length > 0)
      }
    })
  )
}

export function getDescendants (
  nodes: TransformedDocumentSection['nodes'],
  sectionId: string
): Array<{ title: string, sectionId: string, path: string }> {
  const node = nodes[sectionId]
  if (node === undefined) {
    return []
  }
  const childrenTitles = (node.childSections ?? [])
    .map(childId => getDescendants(nodes, childId))
    .flat()
  if (node.childSections === undefined || node.childSections.length === 0) {
    const [sectionId, focusedId] = node.path
    const path = url(DOCUMENTS_CONTENT, { aol: AOL_CORPORATE, sectionId, focusedId })
    return [{ title: node.title, sectionId, path }]
  }
  return childrenTitles
}
